import {
  GameState,
  GameConfig,
  GameStatus,
  Tile,
  ScoreEvent,
  MovementMode,
  DEFAULT_SYMBOLS
} from './types';
import {
  generateBoard,
  canMatch,
  removeTiles,
  isBoardCleared,
  getValidMatches,
  getRemainingTileCount,
  hasValidMatches,
  shuffleRemainingTiles,
  regenerateMatchedTiles
} from './boardGenerator';

/**
 * Game Engine for Snap Pairs
 * Manages game state, scoring, timer, and game flow
 */

export class GameEngine {
  private state: GameState;
  private config: GameConfig;
  private timerInterval: NodeJS.Timeout | null = null;
  private onStateChange?: (state: GameState) => void;
  private onScoreEvent?: (event: ScoreEvent) => void;
  private onShuffleEvent?: () => void;

  constructor(config: Partial<GameConfig> = {}) {
    this.config = {
      gridSize: config.gridSize || 6,
      roundDuration: config.roundDuration || 60,
      movementMode: config.movementMode || MovementMode.Static,
      symbols: config.symbols || DEFAULT_SYMBOLS,
      enableRegeneration: config.enableRegeneration || false
    };

    this.state = this.createInitialState();
  }

  private createInitialState(): GameState {
    return {
      board: { tiles: new Map(), gridSize: this.config.gridSize },
      score: 0,
      timeRemaining: this.config.roundDuration,
      status: GameStatus.NotStarted,
      selectedTile: null,
      movementMode: this.config.movementMode,
      lastMatchTime: 0,
      matchCount: 0
    };
  }

  /**
   * Register callback for state changes
   */
  public onUpdate(callback: (state: GameState) => void): void {
    this.onStateChange = callback;
  }

  /**
   * Register callback for score events
   */
  public onScore(callback: (event: ScoreEvent) => void): void {
    this.onScoreEvent = callback;
  }

  /**
   * Register callback for shuffle events
   */
  public onShuffle(callback: () => void): void {
    this.onShuffleEvent = callback;
  }

  /**
   * Get current game state
   */
  public getState(): GameState {
    return { ...this.state };
  }

  /**
   * Start the game
   */
  public start(): void {
    if (this.state.status === GameStatus.Playing) return;

    this.state = {
      ...this.state,
      board: generateBoard(this.config.gridSize, this.config.symbols),
      status: GameStatus.Playing,
      timeRemaining: this.config.roundDuration,
      lastMatchTime: Date.now()
    };

    this.startTimer();
    this.notifyStateChange();
  }

  /**
   * Pause the game
   */
  public pause(): void {
    if (this.state.status !== GameStatus.Playing) return;

    this.state = {
      ...this.state,
      status: GameStatus.Paused
    };

    this.stopTimer();
    this.notifyStateChange();
  }

  /**
   * Resume the game
   */
  public resume(): void {
    if (this.state.status !== GameStatus.Paused) return;

    this.state = {
      ...this.state,
      status: GameStatus.Playing
    };

    this.startTimer();
    this.notifyStateChange();
  }

  /**
   * Reset the game
   */
  public reset(): void {
    this.stopTimer();
    this.state = this.createInitialState();
    this.notifyStateChange();
  }

  /**
   * Check if board has valid matches, and shuffle if not
   */
  public checkAndShuffleIfNeeded(): void {
    if (this.state.status === GameStatus.Playing && !hasValidMatches(this.state.board)) {
      this.autoShuffle();
    }
  }

  /**
   * Apply a penalty to the score
   */
  public applyPenalty(points: number): void {
    if (this.state.status !== GameStatus.Playing) return;

    this.state = {
      ...this.state,
      score: Math.max(0, this.state.score - points)
    };

    this.notifyStateChange();
  }

  /**
   * Handle tile selection
   */
  public selectTile(tile: Tile): boolean {
    if (this.state.status !== GameStatus.Playing) return false;
    if (tile.matched) return false;

    // If there's already a selected tile, check if this is a match attempt
    if (this.state.selectedTile) {
      // Same tile - deselect it
      if (this.state.selectedTile.id === tile.id) {
        this.state = {
          ...this.state,
          selectedTile: null
        };
        this.notifyStateChange();
        return true;
      }

      // Different tile - try to match
      if (canMatch(this.state.board, this.state.selectedTile, tile)) {
        this.matchTiles(this.state.selectedTile, tile);
        return true;
      } else {
        // Cannot match - apply penalty
        const validMatches = getValidMatches(this.state.board, tile);

        if (validMatches.length === 0) {
          // Clicked tile has no valid matches at all - apply penalty and deselect
          this.state = {
            ...this.state,
            selectedTile: null,
            score: Math.max(0, this.state.score - 10)
          };
        } else {
          // Clicked tile has valid matches, just not with the selected tile - apply penalty and select new tile
          this.state = {
            ...this.state,
            selectedTile: tile,
            score: Math.max(0, this.state.score - 10)
          };
        }
        this.notifyStateChange();
        return false;
      }
    }

    // No tile selected yet - check if this tile can be matched at all
    const validMatches = getValidMatches(this.state.board, tile);

    if (validMatches.length === 0) {
      // Tile cannot be matched with anything - apply penalty and don't select it
      this.state = {
        ...this.state,
        selectedTile: null,
        score: Math.max(0, this.state.score - 10)
      };
      this.notifyStateChange();
      return false;
    }

    // First tile selected - has valid matches, no penalty
    this.state = {
      ...this.state,
      selectedTile: tile
    };
    this.notifyStateChange();
    return true;
  }

  /**
   * Match two tiles
   */
  private matchTiles(tile1: Tile, tile2: Tile): void {
    const now = Date.now();
    const timeSinceLastMatch = (now - this.state.lastMatchTime) / 1000;

    // Calculate score
    const basePoints = 100;
    let comboBonus = 0;

    if (timeSinceLastMatch < 1) {
      comboBonus = 50;
    } else if (timeSinceLastMatch < 3) {
      comboBonus = 25;
    }

    // Update board - either remove tiles or regenerate them
    let newBoard;
    if (this.config.enableRegeneration) {
      // Infinity mode with regeneration - replace matched tiles with new ones
      newBoard = regenerateMatchedTiles(this.state.board, tile1, tile2, this.config.symbols);
    } else {
      // Normal mode - mark tiles as matched
      newBoard = removeTiles(this.state.board, tile1, tile2);
    }

    // Check for clear bonus (only in non-regeneration mode)
    const clearBonus = !this.config.enableRegeneration && isBoardCleared(newBoard) ? 500 : 0;

    const scoreEvent: ScoreEvent = {
      basePoints,
      comboBonus,
      clearBonus,
      total: basePoints + comboBonus + clearBonus
    };

    // Update state
    this.state = {
      ...this.state,
      board: newBoard,
      score: this.state.score + scoreEvent.total,
      selectedTile: null,
      lastMatchTime: now,
      matchCount: this.state.matchCount + 1
    };

    // Notify callbacks
    if (this.onScoreEvent) {
      this.onScoreEvent(scoreEvent);
    }

    // Check win condition (only in non-regeneration mode)
    if (!this.config.enableRegeneration && isBoardCleared(newBoard)) {
      this.handleWin();
    } else {
      // Check if auto-shuffle is needed
      if (!hasValidMatches(this.state.board)) {
        this.autoShuffle();
      } else {
        this.notifyStateChange();
      }
    }
  }

  /**
   * Auto-shuffle tiles when no valid matches exist
   */
  private autoShuffle(): void {
    let shuffledBoard = shuffleRemainingTiles(this.state.board);

    let attempts = 0;
    const maxAttempts = 100;

    while (!hasValidMatches(shuffledBoard) && attempts < maxAttempts) {
      shuffledBoard = shuffleRemainingTiles(shuffledBoard);
      attempts++;
    }

    this.state = {
      ...this.state,
      board: shuffledBoard
    };

    if (this.onShuffleEvent) {
      this.onShuffleEvent();
    }

    this.notifyStateChange();
  }

  /**
   * Handle win condition
   */
  private handleWin(): void {
    this.stopTimer();
    this.state = {
      ...this.state,
      status: GameStatus.Won
    };
    this.notifyStateChange();
  }

  /**
   * Handle game over
   */
  private handleGameOver(): void {
    this.stopTimer();
    this.state = {
      ...this.state,
      status: GameStatus.GameOver,
      selectedTile: null
    };
    this.notifyStateChange();
  }

  /**
   * Start the countdown timer
   */
  private startTimer(): void {
    this.stopTimer();

    this.timerInterval = setInterval(() => {
      if (this.state.status !== GameStatus.Playing) {
        this.stopTimer();
        return;
      }

      const newTimeRemaining = Math.max(0, this.state.timeRemaining - 1);

      this.state = {
        ...this.state,
        timeRemaining: newTimeRemaining
      };

      if (newTimeRemaining <= 0) {
        this.handleGameOver();
      } else {
        this.notifyStateChange();
      }
    }, 1000);
  }

  /**
   * Stop the countdown timer
   */
  private stopTimer(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  /**
   * Notify state change
   */
  private notifyStateChange(): void {
    if (this.onStateChange) {
      this.onStateChange(this.getState());
    }
  }

  /**
   * Get statistics
   */
  public getStats() {
    return {
      matchCount: this.state.matchCount,
      remainingTiles: getRemainingTileCount(this.state.board),
      score: this.state.score,
      timeRemaining: this.state.timeRemaining,
      timeElapsed: this.config.roundDuration - this.state.timeRemaining
    };
  }

  /**
   * Clean up resources
   */
  public destroy(): void {
    this.stopTimer();
  }
}
